import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { resolveImg } from "@/lib/img";
import { DEFAULT_PRODUCTS } from "@/config/site.config";

export default function MarketplacePage() {
  const [products, setProducts] = useState(DEFAULT_PRODUCTS);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    api.get("/products").then(({ data }) => {
      const fromApi = (data || []).filter((p) => p.active !== false);
      const byId = new Map(DEFAULT_PRODUCTS.map((p) => [p.id, p]));
      fromApi.forEach((p) => byId.set(p.id, p));
      setProducts(Array.from(byId.values()).sort((a, b) => (a.order || 99) - (b.order || 99)));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen container-px py-32" data-testid="marketplace-page">
      <div className="mx-auto max-w-7xl">
        <div className="eyebrow text-gold-300">Product Marketplace</div>
        <h1 className="mt-2 font-serif text-4xl md:text-6xl text-white tracking-tight leading-[1.05]">
          Every AIBoxHub <span className="gold-text italic">product</span>.
        </h1>
        <p className="mt-4 text-white/55 max-w-2xl">
          A growing catalogue of AI-native SaaS products engineered for premium businesses. New modules
          are added continuously — no redeploy required.
        </p>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p) => (
            <Link
              to={p.link || "/register"}
              key={p.id}
              data-testid={`mp-card-${p.id}`}
              className="group relative overflow-hidden rounded-3xl border border-white/8 bg-ink-200/60 hover:border-gold-500/35 transition-all"
            >
              {p.image && (
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={resolveImg(p.image)}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover opacity-75 transition-transform duration-700 group-hover:scale-105 group-hover:opacity-95"
                  />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-300">{p.tag || "Product"}</span>
                  <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-gold-300 transition-colors" />
                </div>
                <div className="mt-3 font-serif text-2xl text-white">{p.name}</div>
                <p className="mt-2 text-sm text-white/55 min-h-[44px]">{p.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  {typeof p.price_from === "number" && p.price_from > 0 ? (
                    <span className="text-xs text-white/45 font-mono">
                      From <span className="gold-text font-serif text-base">${p.price_from}</span>/mo
                    </span>
                  ) : <span />}
                  <span className="inline-flex items-center gap-1 text-sm text-gold-300">
                    {p.cta || "Explore"} <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
